import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import ProjectsTable from './ProjectsTable';
import CustomSelect from '../ui/CustomSelect';

const PROMPT_TYPE_OPTIONS = [
  { value: 'all', label: 'All Types' },
  { value: 'System User', label: 'System + User' },
  { value: 'User Only', label: 'User Only' },
  { value: 'few-shot', label: 'Few-Shot Examples' },
];

const RefinePromptPage = ({ project, onBack, onSelectProject, projects, loading }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPromptType, setSelectedPromptType] = useState('all');
  
  const completedProjects = useMemo(() => {
    return projects?.filter(p => p.status === 'complete') || [];
  }, [projects]);

  // Filter projects based on search query and prompt type
  const filteredProjects = useMemo(() => {
    return completedProjects.filter(proj => {
      // Filter by prompt type
      const projectType = proj.user_input?.type || proj.prompt_type || '';
      let typeMatch = selectedPromptType === 'all';
      
      if (!typeMatch) {
        if (selectedPromptType === 'few-shot') {
          typeMatch = projectType.toLowerCase().includes('assistant') || 
                      projectType.toLowerCase().includes('few') ||
                      projectType.includes('System  User  Assistant');
        } else {
          typeMatch = projectType === selectedPromptType;
        }
      }
      
      // Filter by search query (search in name and description)
      const searchLower = searchQuery.toLowerCase();
      const nameMatch = proj.project_name?.toLowerCase().includes(searchLower);
      const descMatch = proj.user_input?.description?.toLowerCase().includes(searchLower);
      const searchMatch = !searchQuery || nameMatch || descMatch;

      return typeMatch && searchMatch;
    });
  }, [completedProjects, selectedPromptType, searchQuery]);

  const hasActiveFilters = selectedPromptType !== 'all' || searchQuery;

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedPromptType('all');
  };

  const handleSelectProjectFromList = (proj) => {
    if (onSelectProject) {
      onSelectProject(proj);
    }
    // Navigate to refinement page for this project
    navigate(`/refine-prompt/${proj.id}`);
  };

  const handleBackToProjects = () => {
    navigate('/prompts');
  };

  // If a project is already selected (coming from project detail)
  if (project) {
    return null; // RefinementPage will be shown instead
  }

  if (loading) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-4 p-10">
        <div className="doc-loading-spinner"></div>
        <p className="text-text-secondary text-sm">Loading prompts...</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-transparent overflow-hidden">
      {/* Document Header */}
      <div className="doc-page-header">
        <div className="doc-header-left">
          <div className="doc-header-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M12 20h9"></path>
              <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
            </svg>
          </div>
          <div className="doc-header-content">
            <h1 className="doc-header-title">Refine Prompts</h1>
            <p className="doc-header-subtitle">
              <span className="doc-count">{filteredProjects.length}</span>
              {filteredProjects.length === 1 ? ' prompt' : ' prompts'}
              {hasActiveFilters && completedProjects.length !== filteredProjects.length && (
                <span className="text-text-tertiary"> of {completedProjects.length}</span>
              )}
            </p>
          </div>
        </div>
        
        <div className="doc-header-actions">
          <button onClick={handleBackToProjects} className="doc-new-btn">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
            </svg>
            <span className="hidden sm:inline">Go to Prompts</span>
            <span className="sm:hidden">Prompts</span>
          </button>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="px-4 py-3 border-b border-border-primary/50 flex flex-col sm:flex-row gap-3 sm:items-center">
        {/* Search Input */}
        <div className="relative w-full sm:w-auto sm:min-w-[300px] sm:max-w-[480px]">
          <svg 
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary pointer-events-none" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="8"/>
            <path d="m21 21-4.35-4.35"/>
          </svg>
          <input
            type="text"
            placeholder="Search prompts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-bg-secondary border border-border-primary rounded-lg text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none transition-all"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-secondary transition-colors"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          )}
        </div>

        {/* Prompt Type Filter */}
        <div className="relative flex items-center gap-2">
          <div className="flex items-center gap-2 text-text-tertiary">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/>
            </svg>
            <span className="text-xs font-medium hidden sm:inline">Filter:</span>
          </div>
          <CustomSelect
            value={selectedPromptType}
            onChange={(e) => setSelectedPromptType(e.target.value)}
            options={PROMPT_TYPE_OPTIONS}
            placeholder="All Types"
            size="small"
          />
        </div>

        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="px-3 py-2 text-xs font-medium text-text-secondary hover:text-text-primary bg-bg-tertiary hover:bg-bg-secondary border border-border-primary rounded-lg transition-all flex items-center gap-1.5"
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 18L18 6M6 6l12 12"/>
            </svg>
            Clear
          </button>
        )}
      </div>
      
      {/* Document List */}
      <ProjectsTable
        projects={filteredProjects}
        onSelectProject={handleSelectProjectFromList}
        emptyStateConfig={{
          title: hasActiveFilters ? 'No Matching Prompts' : 'No Prompts Available',
          description: hasActiveFilters 
            ? 'No prompts match your current filters. Try adjusting your search or filter criteria.'
            : 'Create and complete your first prompt to start refining. Refinement helps you improve and iterate on your prompt templates.',
          icon: (props) => (
            <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 20h9"></path>
              <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
            </svg>
          ),
          actionLabel: hasActiveFilters ? 'Clear Filters' : 'Go to Prompts',
          onAction: hasActiveFilters ? clearFilters : handleBackToProjects
        }}
      />
    </div>
  );
};

RefinePromptPage.propTypes = {
  project: PropTypes.object,
  onBack: PropTypes.func,
  onSelectProject: PropTypes.func,
  projects: PropTypes.array,
  loading: PropTypes.bool,
};

RefinePromptPage.defaultProps = {
  project: null,
  onBack: null,
  onSelectProject: null,
  projects: [],
  loading: false,
};

export default RefinePromptPage;
